import { useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Wizard, type WizardSchritt } from '../components/Wizard';
import { Button } from '../components/ui/Button';
import { cn } from '../lib/cn';
import { DEMO, api, ApiFehler } from '../lib/api';
import { formatQm, formatZahl, parseZahlEingabe } from '../lib/format';
import {
  type Benennung,
  type Einheit,
  type ObjektKopf,
  type Struktur,
  type VorlageEinheit,
  DACHGESCHOSS,
  einheitenAusVorlage,
  etagen,
  geschossKurz,
  geschossLang,
} from '../lib/haus';

/**
 * Etagen-Assistent: Aufbau des Hauses festlegen und
 *   - bei einem leeren Objekt die Einheiten aus einer Vorlage erzeugen (Etagen × Einheiten),
 *   - bei bestehenden Einheiten jede Einheit ihrer Etage und Lage zuordnen.
 */

const feldKlasse =
  'w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg outline-none focus:border-accent';

const LAGEN = ['links', 'mitte links', 'mitte', 'mitte rechts', 'rechts'];

export interface EtagenAssistentProps {
  objekt: ObjektKopf;
  bestehende: Einheit[];
  onFertig: (ergebnis: { struktur: Struktur; einheiten: Einheit[] }) => void;
  onAbbruch: () => void;
  /** Wohn- und Geschäftshaus: Einheiten im Erdgeschoss als Gewerbe vorbelegen. */
  gewerbeImEg?: boolean;
}

interface Zuordnung {
  geschoss: number | null;
  position: string | null;
}

function strukturVorschlag(objekt: ObjektKopf, bestehende: Einheit[]): Struktur {
  if (objekt.obergeschosse !== null) return objekt;
  const og = Math.max(0, ...bestehende.map((e) => (e.geschoss !== null && e.geschoss < 60 ? e.geschoss : 0)));
  return {
    untergeschosse: objekt.untergeschosse,
    obergeschosse: bestehende.length > 0 ? og : 2,
    dachgeschoss: objekt.dachgeschoss || bestehende.some((e) => e.geschoss === DACHGESCHOSS),
  };
}

function parseFlaeche(roh: string): number | null {
  const n = parseZahlEingabe(roh);
  return n !== null && n > 0 ? n : null;
}

export function EtagenAssistent({ objekt, bestehende, onFertig, onAbbruch, gewerbeImEg = false }: EtagenAssistentProps) {
  const modus: 'vorlage' | 'zuordnung' = bestehende.length === 0 ? 'vorlage' : 'zuordnung';
  const start = strukturVorschlag(objekt, bestehende);

  const [ug, setUg] = useState(start.untergeschosse);
  const [og, setOg] = useState(start.obergeschosse ?? 2);
  const [dg, setDg] = useState(start.dachgeschoss);
  const struktur: Struktur = { untergeschosse: ug, obergeschosse: og, dachgeschoss: dg };
  const liste = etagen(struktur);

  // Vorlage
  const [standard, setStandard] = useState(2);
  const [abweichend, setAbweichend] = useState<Record<number, number>>({});
  const [benennung, setBenennung] = useState<Benennung>('lage');
  const jeEtage = useMemo(() => {
    const r: Record<number, number> = {};
    for (const g of liste) r[g] = abweichend[g] ?? (g < 0 ? 0 : g === DACHGESCHOSS ? Math.min(standard, 1) : standard);
    return r;
  }, [liste.join(','), standard, abweichend]); // eslint-disable-line react-hooks/exhaustive-deps
  const vorlage = useMemo(() => einheitenAusVorlage(struktur, jeEtage, benennung), [ug, og, dg, jeEtage, benennung]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bearbeitbare Angaben je Vorlage-Einheit (Bezeichnung, Fläche, Nutzung) über einen stabilen Schlüssel.
  const [eingaben, setEingaben] = useState<Record<string, { bezeichnung?: string; flaeche?: string; gewerbe?: boolean }>>({});

  // Zuordnung
  const [zuordnung, setZuordnung] = useState<Record<string, Zuordnung>>(() =>
    Object.fromEntries(bestehende.map((e) => [e.id, { geschoss: e.geschoss, position: e.position }])),
  );

  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  function zeile(v: VorlageEinheit) {
    const e = eingaben[v.schluessel] ?? {};
    return {
      bezeichnung: e.bezeichnung ?? v.bezeichnung,
      flaeche: e.flaeche ?? '',
      gewerbe: e.gewerbe ?? (gewerbeImEg && v.geschoss === 0),
    };
  }
  const summeQm = vorlage.reduce((s, v) => s + (parseFlaeche(zeile(v).flaeche) ?? 0), 0);

  function gleichmaessigVerteilen() {
    if (vorlage.length === 0) return;
    const je = Math.floor((objekt.gesamtflaecheQm / vorlage.length) * 100) / 100;
    const rest = Math.round((objekt.gesamtflaecheQm - je * vorlage.length) * 100) / 100;
    setEingaben((alt) => {
      const neu = { ...alt };
      vorlage.forEach((v, i) => {
        const wert = i === 0 ? je + rest : je;
        neu[v.schluessel] = { ...neu[v.schluessel], flaeche: formatZahl(Math.round(wert * 100) / 100) };
      });
      return neu;
    });
  }

  async function speichern() {
    setFehler(null);
    setSpeichert(true);
    try {
      const neueEinheiten =
        modus === 'vorlage'
          ? vorlage.map((v) => {
              const z = zeile(v);
              return {
                bezeichnung: z.bezeichnung.trim(),
                geschoss: v.geschoss,
                position: v.position,
                reihenfolge: v.reihenfolge,
                wohnflaecheQm: parseFlaeche(z.flaeche) as number,
                nutzungsart: (z.gewerbe ? 'gewerbe' : 'wohnen') as 'wohnen' | 'gewerbe',
              };
            })
          : [];
      const zuordnungen =
        modus === 'zuordnung'
          ? bestehende.map((e) => {
              const z = zuordnung[e.id] ?? { geschoss: null, position: null };
              return {
                einheitId: e.id,
                geschoss: z.geschoss,
                position: z.position,
                reihenfolge: z.position ? LAGEN.indexOf(z.position) + 1 : 0,
              };
            })
          : [];

      let einheiten: Einheit[];
      if (DEMO) {
        einheiten =
          modus === 'vorlage'
            ? neueEinheiten.map((n, i) => ({
                id: `neu-${Date.now()}-${i}`,
                objektId: objekt.id,
                zimmer: null,
                lage: null,
                megAnteil: null,
                eigennutzung: false,
                ...n,
              }))
            : bestehende.map((e) => {
                const z = zuordnungen.find((x) => x.einheitId === e.id)!;
                return { ...e, geschoss: z.geschoss, position: z.position, reihenfolge: z.reihenfolge };
              });
      } else {
        const r = await api.post<{ einheiten: Einheit[] }>(`/objekte/${objekt.id}/etagen`, {
          untergeschosse: ug,
          obergeschosse: og,
          dachgeschoss: dg,
          neueEinheiten,
          zuordnungen,
        });
        einheiten = r.einheiten;
      }
      onFertig({ struktur, einheiten });
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Die Etagen konnten nicht gespeichert werden.');
      setSpeichert(false);
    }
  }

  const aufbau: WizardSchritt = {
    id: 'aufbau',
    titel: 'Wie ist das Haus aufgebaut?',
    kurz: 'Aufbau',
    hinweis: 'Geben Sie die Geschosse an. Das Erdgeschoss ist immer vorhanden.',
    inhalt: (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_11rem]">
        <div className="space-y-3">
          <Stepper
            label="Obergeschosse"
            hinweis="Vollgeschosse über dem Erdgeschoss"
            wert={og}
            min={0}
            max={60}
            onWert={setOg}
          />
          <Schalter
            label="Ausgebautes Dachgeschoss"
            hinweis="Wohnungen unter dem Dach"
            an={dg}
            onWechsel={setDg}
          />
          <Stepper
            label="Untergeschosse"
            hinweis="Keller oder Souterrain"
            wert={ug}
            min={0}
            max={3}
            onWert={setUg}
          />
        </div>
        <MiniHaus struktur={struktur} />
      </div>
    ),
  };

  const schritte: WizardSchritt[] =
    modus === 'vorlage'
      ? [
          aufbau,
          {
            id: 'einheiten',
            titel: 'Wie viele Einheiten liegen auf jeder Etage?',
            kurz: 'Einheiten',
            hinweis: 'Ein Wert für alle Etagen; einzelne Etagen lassen sich anpassen.',
            inhalt: (
              <div className="space-y-4">
                <Stepper label="Einheiten je Etage" wert={standard} min={0} max={20} onWert={(n) => { setStandard(n); setAbweichend({}); }} />
                <div className="rounded-lg border border-border">
                  {liste.map((g) => (
                    <div key={g} className="flex items-center justify-between gap-3 border-b border-border px-3 py-2 last:border-0">
                      <span className="text-sm text-fg">{geschossLang(g)}</span>
                      <MiniStepper
                        wert={jeEtage[g] ?? 0}
                        min={0}
                        max={20}
                        label={geschossLang(g)}
                        onWert={(n) => setAbweichend((a) => ({ ...a, [g]: n }))}
                      />
                    </div>
                  ))}
                </div>
                <fieldset>
                  <legend className="mb-1.5 text-sm font-medium text-fg">Bezeichnung der Einheiten</legend>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <Wahl an={benennung === 'lage'} onClick={() => setBenennung('lage')} titel="Nach Lage" text="EG links, 1. OG rechts …" />
                    <Wahl an={benennung === 'nummer'} onClick={() => setBenennung('nummer')} titel="Durchnummeriert" text="Whg. 1, Whg. 2 … (von unten)" />
                  </div>
                </fieldset>
                <p className="text-sm text-fg-muted">
                  Ergibt <span className="font-semibold text-fg">{vorlage.length}</span>{' '}
                  {vorlage.length === 1 ? 'Einheit' : 'Einheiten'}.
                </p>
              </div>
            ),
            pruefen: () => (vorlage.length === 0 ? ['Bitte mindestens eine Einheit vorsehen.'] : []),
          },
          {
            id: 'flaechen',
            titel: 'Wohnflächen erfassen',
            kurz: 'Flächen',
            hinweis:
              'Die Wohnfläche ist der Standard-Umlageschlüssel. Bitte die Werte aus Mietvertrag oder Flächenberechnung übernehmen.',
            inhalt: (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm text-fg-muted">
                    Summe <span className="font-semibold text-fg tabellenziffern">{formatQm(Math.round(summeQm * 100) / 100)}</span>{' '}
                    von {formatQm(objekt.gesamtflaecheQm)} Gesamtfläche
                  </p>
                  <Button variante="dezent" groesse="sm" onClick={gleichmaessigVerteilen}>
                    Gleichmäßig verteilen
                  </Button>
                </div>
                <div className="divide-y divide-border rounded-lg border border-border">
                  {vorlage.map((v) => {
                    const z = zeile(v);
                    return (
                      <div key={v.schluessel} className="grid grid-cols-[1fr_6.5rem] items-center gap-2 px-3 py-2 sm:grid-cols-[4rem_1fr_7rem_7rem]">
                        <span className="hidden text-xs font-semibold text-fg-subtle sm:block">{geschossKurz(v.geschoss)}</span>
                        <input
                          aria-label="Bezeichnung"
                          className={feldKlasse}
                          value={z.bezeichnung}
                          onChange={(e) => setEingaben((a) => ({ ...a, [v.schluessel]: { ...a[v.schluessel], bezeichnung: e.target.value } }))}
                        />
                        <label className="relative block">
                          <span className="sr-only">Wohnfläche in m²</span>
                          <input
                            inputMode="decimal"
                            placeholder="m²"
                            className={cn(feldKlasse, 'pr-8 text-right tabellenziffern')}
                            value={z.flaeche}
                            onChange={(e) => setEingaben((a) => ({ ...a, [v.schluessel]: { ...a[v.schluessel], flaeche: e.target.value } }))}
                          />
                          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-fg-subtle">m²</span>
                        </label>
                        <select
                          aria-label="Nutzung"
                          className={cn(feldKlasse, 'col-span-2 sm:col-span-1')}
                          value={z.gewerbe ? 'gewerbe' : 'wohnen'}
                          onChange={(e) => setEingaben((a) => ({ ...a, [v.schluessel]: { ...a[v.schluessel], gewerbe: e.target.value === 'gewerbe' } }))}
                        >
                          <option value="wohnen">Wohnen</option>
                          <option value="gewerbe">Gewerbe</option>
                        </select>
                      </div>
                    );
                  })}
                </div>
                {summeQm > 0 && Math.abs(summeQm - objekt.gesamtflaecheQm) > 0.5 && (
                  <p className="rounded-lg bg-warning-soft px-3 py-2 text-sm text-warning-soft-fg">
                    Die Summe weicht um {formatQm(Math.round(Math.abs(summeQm - objekt.gesamtflaecheQm) * 100) / 100)} von
                    der Gesamtfläche des Objekts ab. Bei der Umlage nach Wohnfläche zählt die Summe der Einheiten.
                  </p>
                )}
              </div>
            ),
            pruefen: () => {
              const f: string[] = [];
              for (const v of vorlage) {
                const z = zeile(v);
                if (!z.bezeichnung.trim()) f.push(`${geschossKurz(v.geschoss)}: Bitte eine Bezeichnung angeben.`);
                if (parseFlaeche(z.flaeche) === null) f.push(`${z.bezeichnung || geschossKurz(v.geschoss)}: Bitte die Wohnfläche angeben.`);
              }
              return f.slice(0, 6);
            },
          },
        ]
      : [
          aufbau,
          {
            id: 'zuordnung',
            titel: 'Einheiten den Etagen zuordnen',
            kurz: 'Zuordnung',
            hinweis: 'Wählen Sie für jede Einheit Etage und Lage. So entsteht die Hausansicht.',
            inhalt: (
              <div className="divide-y divide-border rounded-lg border border-border">
                {bestehende.map((e) => {
                  const z = zuordnung[e.id] ?? { geschoss: null, position: null };
                  const ungueltig = z.geschoss !== null && !liste.includes(z.geschoss);
                  return (
                    <div key={e.id} className="grid grid-cols-1 gap-2 px-3 py-2.5 sm:grid-cols-[1fr_10rem_9rem] sm:items-center">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-fg">{e.bezeichnung}</p>
                        <p className="text-xs text-fg-subtle">{formatQm(e.wohnflaecheQm)}</p>
                      </div>
                      <select
                        aria-label={`Etage für ${e.bezeichnung}`}
                        className={cn(feldKlasse, ungueltig && 'border-danger')}
                        value={z.geschoss === null ? '' : String(z.geschoss)}
                        onChange={(ev) =>
                          setZuordnung((a) => ({ ...a, [e.id]: { ...z, geschoss: ev.target.value === '' ? null : Number(ev.target.value) } }))
                        }
                      >
                        <option value="">Ohne Etage</option>
                        {liste.map((g) => (
                          <option key={g} value={g}>
                            {geschossLang(g)}
                          </option>
                        ))}
                        {ungueltig && <option value={String(z.geschoss)}>{geschossLang(z.geschoss)} (entfällt)</option>}
                      </select>
                      <select
                        aria-label={`Lage für ${e.bezeichnung}`}
                        className={feldKlasse}
                        value={z.position ?? ''}
                        onChange={(ev) => setZuordnung((a) => ({ ...a, [e.id]: { ...z, position: ev.target.value || null } }))}
                      >
                        <option value="">Lage offen</option>
                        {LAGEN.map((l) => (
                          <option key={l} value={l}>
                            {l}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            ),
            pruefen: () =>
              bestehende
                .filter((e) => {
                  const g = zuordnung[e.id]?.geschoss ?? null;
                  return g !== null && !liste.includes(g);
                })
                .map((e) => `${e.bezeichnung}: Die gewählte Etage ist im Aufbau nicht mehr vorhanden.`),
          },
        ];

  return (
    <div className="space-y-3">
      <Wizard
        schritte={schritte}
        abschlussLabel={speichert ? 'Wird gespeichert …' : modus === 'vorlage' ? `${vorlage.length} Einheiten anlegen` : 'Zuordnung speichern'}
        onAbschluss={() => {
          if (!speichert) void speichern();
        }}
        onAbbruch={onAbbruch}
      />
      {fehler && (
        <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger-soft-fg">
          {fehler}
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Bausteine */

function Stepper({
  label,
  hinweis,
  wert,
  min,
  max,
  onWert,
}: {
  label: string;
  hinweis?: string;
  wert: number;
  min: number;
  max: number;
  onWert: (n: number) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5">
      <div className="min-w-0">
        <p className="text-sm font-medium text-fg">{label}</p>
        {hinweis && <p className="text-xs text-fg-muted">{hinweis}</p>}
      </div>
      <MiniStepper wert={wert} min={min} max={max} label={label} onWert={onWert} />
    </div>
  );
}

function MiniStepper({
  wert,
  min,
  max,
  label,
  onWert,
}: {
  wert: number;
  min: number;
  max: number;
  label: string;
  onWert: (n: number) => void;
}) {
  const reduziert = useReducedMotion();
  const knopf =
    'flex h-9 w-9 items-center justify-center rounded-md border border-border-strong text-lg text-fg transition-colors ' +
    'hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus';
  return (
    <div className="flex shrink-0 items-center gap-2" role="group" aria-label={label}>
      <button type="button" className={knopf} onClick={() => onWert(Math.max(min, wert - 1))} disabled={wert <= min} aria-label={`${label} verringern`}>
        −
      </button>
      <motion.span
        key={wert}
        initial={reduziert ? false : { scale: 0.8, opacity: 0.4 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-7 text-center text-base font-semibold tabellenziffern text-fg"
        aria-live="polite"
      >
        {wert}
      </motion.span>
      <button type="button" className={knopf} onClick={() => onWert(Math.min(max, wert + 1))} disabled={wert >= max} aria-label={`${label} erhöhen`}>
        +
      </button>
    </div>
  );
}

function Schalter({ label, hinweis, an, onWechsel }: { label: string; hinweis?: string; an: boolean; onWechsel: (an: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={an}
      onClick={() => onWechsel(!an)}
      className="flex w-full items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
    >
      <span className="min-w-0">
        <span className="block text-sm font-medium text-fg">{label}</span>
        {hinweis && <span className="block text-xs text-fg-muted">{hinweis}</span>}
      </span>
      <span className={cn('relative h-6 w-11 shrink-0 rounded-pill transition-colors', an ? 'bg-accent' : 'bg-surface-3')}>
        <motion.span
          layout
          transition={{ type: 'spring', stiffness: 600, damping: 34 }}
          className={cn('absolute top-0.5 h-5 w-5 rounded-pill bg-surface shadow-sm', an ? 'right-0.5' : 'left-0.5')}
        />
      </span>
    </button>
  );
}

function Wahl({ an, onClick, titel, text }: { an: boolean; onClick: () => void; titel: string; text: string }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={an}
      onClick={onClick}
      className={cn(
        'rounded-lg border px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
        an ? 'border-accent bg-accent-soft' : 'border-border-strong hover:bg-surface-2',
      )}
    >
      <span className={cn('block text-sm font-semibold', an ? 'text-accent-soft-fg' : 'text-fg')}>{titel}</span>
      <span className="block text-xs text-fg-muted">{text}</span>
    </button>
  );
}

/** Kleine Live-Skizze des Hauses neben den Eingaben. */
function MiniHaus({ struktur }: { struktur: Struktur }) {
  const liste = etagen(struktur);
  const sichtbar = liste.length > 12 ? [...liste.slice(0, 5), 'luecke' as const, ...liste.slice(-5)] : liste;
  return (
    <div className="hidden flex-col items-stretch sm:flex" aria-hidden="true">
      <svg viewBox="0 0 100 18" className="w-full">
        <path d="M4 17 L50 2 L96 17" fill="none" className="stroke-border-strong" strokeWidth="2" strokeLinejoin="round" />
      </svg>
      <div className="mx-2 flex flex-col gap-px overflow-hidden rounded-b-md border border-border bg-border">
        {sichtbar.map((g, i) =>
          g === 'luecke' ? (
            <div key={`l${i}`} className="bg-surface py-0.5 text-center text-[10px] text-fg-subtle">⋮</div>
          ) : (
            <motion.div
              key={g}
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={cn('flex h-6 items-center justify-center text-[11px] font-medium', g < 0 ? 'bg-surface-2 text-fg-subtle' : 'bg-surface text-fg-muted')}
            >
              {geschossKurz(g)}
            </motion.div>
          ),
        )}
      </div>
      <p className="mt-2 text-center text-xs text-fg-subtle">
        {liste.length} {liste.length === 1 ? 'Geschoss' : 'Geschosse'}
      </p>
    </div>
  );
}

